import { type Editor, InputRule, mergeAttributes, Node, type Range } from "@tiptap/core";
import { PluginKey } from "@tiptap/pm/state";
import { NodeViewWrapper, type ReactNodeViewProps, ReactNodeViewRenderer, ReactRenderer } from "@tiptap/react";
import SuggestionPlugin, {
  type SuggestionKeyDownProps,
  type SuggestionOptions,
  type SuggestionProps,
} from "@tiptap/suggestion";
import { forwardRef, type MouseEvent, useEffect, useImperativeHandle, useRef, useState } from "react";
import tippy, { type Instance } from "tippy.js";

export interface TemplateSlotDeclaredParam {
  name: string;
  label?: string;
  type?: "string" | "text" | "boolean" | "enum";
  required?: boolean;
  enumValues?: string[];
}

interface TemplateSlotOptions {
  getDeclaredParams: () => TemplateSlotDeclaredParam[];
  isInsertionEnabled: () => boolean;
}

interface TemplateSlotListProps {
  items: TemplateSlotDeclaredParam[];
  command: (item: TemplateSlotDeclaredParam) => void;
}

interface TemplateSlotListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

export const TEMPLATE_SLOT_NODE_NAME = "templateSlot";
export const TEMPLATE_SLOT_CLASS_NAME = "editor-extension-template-slot";
export const TEMPLATE_SLOT_PARAM_PATTERN = /^[A-Za-z][A-Za-z0-9_]*$/;

const RESERVED_TEMPLATE_SLOT_PARAM_NAMES = new Set(["constructor", "prototype"]);
const TEMPLATE_SLOT_SUGGESTION_LIMIT = 50;
const templateSlotSuggestionPluginKey = new PluginKey("templateSlotSuggestion");
const templateSlotInputRule = /{{([A-Za-z][A-Za-z0-9_]*)}}$/;

const escapeHtml = (value: string): string =>
  value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const normalizeTemplateSlotParam = (value: unknown): string => String(value ?? "").trim();

export const isValidTemplateSlotParamName = (value: string): boolean =>
  value.length > 0 && TEMPLATE_SLOT_PARAM_PATTERN.test(value) && !RESERVED_TEMPLATE_SLOT_PARAM_NAMES.has(value);

export const getTemplateSlotSuggestionItems = (
  declaredParams: TemplateSlotDeclaredParam[],
  query: string
): TemplateSlotDeclaredParam[] => {
  const normalizedQuery = query.replace(/^\{+/, "").toLowerCase();
  const seen = new Set<string>();

  return declaredParams
    .filter((param) => {
      const name = normalizeTemplateSlotParam(param.name);
      if (!isValidTemplateSlotParamName(name) || seen.has(name)) {
        return false;
      }

      seen.add(name);
      return (
        !normalizedQuery ||
        name.toLowerCase().includes(normalizedQuery) ||
        param.label?.toLowerCase().includes(normalizedQuery)
      );
    })
    .map((param) => ({ ...param, name: normalizeTemplateSlotParam(param.name) }))
    .slice(0, TEMPLATE_SLOT_SUGGESTION_LIMIT);
};

const TemplateSlotNodeComponent = (props: ReactNodeViewProps<HTMLSpanElement>) => {
  const { editor, node, getPos } = props;
  const param = normalizeTemplateSlotParam(node.attrs.param);

  const selectNode = (event: MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    editor?.commands.setNodeSelection(getPos() ?? 0);
  };

  return (
    <NodeViewWrapper as="span" onClick={selectNode} className={`${TEMPLATE_SLOT_CLASS_NAME} inline-flex`}>
      <span
        contentEditable={false}
        data-tpl-slot={param}
        className="inline-flex max-w-full select-none items-center rounded-sm border border-primary/25 bg-primary/10 px-1.5 py-0.5 font-mono text-xs leading-none text-primary in-[.ProseMirror-selectednode]:ring-2 in-[.ProseMirror-selectednode]:ring-primary/50 selection:bg-transparent!"
      >
        {param}
      </span>
    </NodeViewWrapper>
  );
};

const TemplateSlotList = forwardRef<TemplateSlotListRef, TemplateSlotListProps>(({ items, command }, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => setSelectedIndex(0), [items]);

  useEffect(() => {
    itemRefs.current[selectedIndex]?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  useImperativeHandle(
    ref,
    () => ({
      onKeyDown: ({ event }) => {
        if (items.length === 0) {
          return false;
        }

        if (event.key === "ArrowUp") {
          event.preventDefault();
          setSelectedIndex((prev) => (prev + items.length - 1) % items.length);
          return true;
        }

        if (event.key === "ArrowDown") {
          event.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % items.length);
          return true;
        }

        if (event.key === "Enter") {
          event.preventDefault();
          const item = items[Math.min(selectedIndex, items.length - 1)];
          if (item) {
            command(item);
          }
          return true;
        }

        return false;
      },
    }),
    [command, items, selectedIndex]
  );

  return (
    <div className="bg-popover rounded-md shadow-md border p-1 max-h-64 overflow-y-auto">
      {items.length > 0 ? (
        items.map((item, index) => (
          <button
            key={item.name}
            ref={(el) => {
              itemRefs.current[index] = el;
            }}
            type="button"
            onClick={() => command(item)}
            className={`relative flex w-full cursor-default select-none items-start gap-2 rounded-sm px-2 py-1.5 text-left text-xs outline-hidden transition-colors ${
              index === selectedIndex ? "bg-accent text-accent-foreground" : "hover:bg-accent/50"
            }`}
          >
            <span className="font-mono font-medium">{item.name}</span>
            {item.label && <span className="truncate text-muted-foreground">{item.label}</span>}
          </button>
        ))
      ) : (
        <div className="px-2 py-1.5 text-xs text-muted-foreground">No template fields</div>
      )}
    </div>
  );
});

TemplateSlotList.displayName = "TemplateSlotList";

const getClientRect = (clientRect: (() => DOMRect | null) | null | undefined): (() => DOMRect) => {
  return () => clientRect?.() ?? new DOMRect(0, 0, 0, 0);
};

export const createTemplateSlotSuggestionConfig = (
  getDeclaredParams: TemplateSlotOptions["getDeclaredParams"]
): Omit<SuggestionOptions<TemplateSlotDeclaredParam, TemplateSlotDeclaredParam>, "editor"> => ({
  char: "{",
  allowedPrefixes: null,
  items: ({ query }) => getTemplateSlotSuggestionItems(getDeclaredParams(), query),
  render: () => {
    let component: ReactRenderer<TemplateSlotListRef, TemplateSlotListProps> | undefined;
    let popup: Instance[] | undefined;
    let hasDestroyedPopup = false;

    return {
      onStart: (props: SuggestionProps<TemplateSlotDeclaredParam, TemplateSlotDeclaredParam>) => {
        hasDestroyedPopup = false;
        component = new ReactRenderer(TemplateSlotList, {
          props: { items: props.items, command: props.command },
          editor: props.editor,
        });

        popup = tippy("body", {
          getReferenceClientRect: getClientRect(props.clientRect),
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: "manual",
          placement: "bottom-start",
        });
      },
      onUpdate: (props: SuggestionProps<TemplateSlotDeclaredParam, TemplateSlotDeclaredParam>) => {
        component?.updateProps({ items: props.items, command: props.command });
        popup?.[0]?.setProps({ getReferenceClientRect: getClientRect(props.clientRect) });
      },
      onKeyDown: (props: SuggestionKeyDownProps) => component?.ref?.onKeyDown(props) ?? false,
      onExit: () => {
        if (!hasDestroyedPopup) {
          popup?.[0]?.destroy();
          hasDestroyedPopup = true;
        }

        component?.destroy();
        component = undefined;
        popup = undefined;
      },
    };
  },
  command: ({ editor, range, props }: { editor: Editor; range: Range; props: TemplateSlotDeclaredParam }) => {
    const param = normalizeTemplateSlotParam(props.name);
    if (!isValidTemplateSlotParamName(param)) {
      return;
    }

    editor.chain().focus().deleteRange(range).insertContent({ type: TEMPLATE_SLOT_NODE_NAME, attrs: { param } }).run();
  },
});

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    TemplateSlot: {
      insertTemplateSlot: (param?: string) => ReturnType;
    };
  }
}

export const EditorExtensionTemplateSlot = Node.create<TemplateSlotOptions>({
  name: TEMPLATE_SLOT_NODE_NAME,
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,

  addOptions() {
    return {
      getDeclaredParams: () => [],
      // Whether the AUTHORING affordances are live: the `{{name}}` input rule and
      // the `{` suggestion menu. Parsing and rendering of existing data-tpl-slot
      // markup are never gated — the node type stays registered in every editor so
      // loading or round-tripping a template can't silently drop slots. This is a
      // function, not a boolean, because Tiptap builds the editor once: reading it
      // at input/suggestion time keeps it live across a playbook type flip.
      isInsertionEnabled: () => true,
    };
  },

  addAttributes() {
    return {
      param: {
        default: "",
        rendered: false,
      },
    };
  },

  addCommands() {
    return {
      insertTemplateSlot:
        (rawParam?: string) =>
        ({ commands }) => {
          const param = normalizeTemplateSlotParam(rawParam);
          if (!isValidTemplateSlotParamName(param)) {
            return false;
          }

          return commands.insertContent({ type: this.name, attrs: { param } });
        },
    };
  },

  parseHTML() {
    return [
      {
        priority: 100,
        tag: "span[data-tpl-slot]",
        getAttrs(node) {
          if (typeof node === "string") {
            return false;
          }

          const param = normalizeTemplateSlotParam(node.getAttribute("data-tpl-slot"));
          if (!isValidTemplateSlotParamName(param)) {
            return false;
          }

          return { param };
        },
      },
    ];
  },

  renderHTML({ node }) {
    const param = normalizeTemplateSlotParam(node.attrs.param);

    return [
      "span",
      mergeAttributes({
        class: TEMPLATE_SLOT_CLASS_NAME,
        "data-tpl-slot": param,
      }),
      param,
    ];
  },

  renderText({ node }) {
    return normalizeTemplateSlotParam(node.attrs.param);
  },

  renderMarkdown({ attrs }) {
    const param = normalizeTemplateSlotParam(attrs?.param);
    if (!param) {
      return "";
    }

    const escapedParam = escapeHtml(param);
    return `<span class="${TEMPLATE_SLOT_CLASS_NAME}" data-tpl-slot="${escapedParam}">${escapedParam}</span>`;
  },

  addInputRules() {
    const type = this.type;
    const isInsertionEnabled = this.options.isInsertionEnabled;

    return [
      new InputRule({
        find: templateSlotInputRule,
        handler: ({ state, range, match }) => {
          // Typing `{{name}}` only creates a slot where slot authoring is offered.
          // In an ordinary Workflow editor it stays literal text.
          if (!isInsertionEnabled()) {
            return null;
          }

          const param = normalizeTemplateSlotParam(match[1]);
          if (!isValidTemplateSlotParamName(param)) {
            return null;
          }

          state.tr.replaceWith(range.from, range.to, type.create({ param })).scrollIntoView();
        },
      }),
    ];
  },

  addProseMirrorPlugins() {
    const isInsertionEnabled = this.options.isInsertionEnabled;

    return [
      SuggestionPlugin({
        editor: this.editor,
        pluginKey: templateSlotSuggestionPluginKey,
        ...createTemplateSlotSuggestionConfig(this.options.getDeclaredParams),
        // Suppress the `{` menu outside slot authoring, so a Workflow editor
        // behaves exactly as it did before templates existed.
        allow: () => isInsertionEnabled(),
      }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(TemplateSlotNodeComponent);
  },
});
